import { Graphics } from "pixi.js";

import { Colors } from "../../config/DesignConstants";
import { LayerName } from "../../config/layers";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { V2d } from "../../core/Vector";

export interface VectorSliderOptions {
  value: number; // 0-1
  min?: number;
  max?: number;
  step?: number;
  width?: number;
  position?: V2d;
  onChange?: (value: number) => void;
  layer?: LayerName;
}

export class VectorSlider extends BaseEntity implements Entity {
  pausable = false; // Sliders should work even when game is paused

  private track: Graphics;
  private fill: Graphics;
  private handle: Graphics;

  private _value: number;
  private min: number;
  private max: number;
  private step: number;
  private sliderWidth: number;
  private trackHeight: number = 6;
  private handleSize: number = 16;

  private onChangeCallback?: (value: number) => void;
  private isDragging: boolean = false;
  private isHovered: boolean = false;

  private bounds: { x: number; y: number; width: number; height: number } = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };

  constructor(options: VectorSliderOptions) {
    super();

    this._value = Math.max(0, Math.min(1, options.value));
    this.min = options.min ?? 0;
    this.max = options.max ?? 1;
    this.step = options.step ?? 0.1;
    this.sliderWidth = options.width ?? 200;
    this.onChangeCallback = options.onChange;

    // Create graphics components
    this.track = new Graphics();
    this.fill = new Graphics();
    this.handle = new Graphics();

    if (options.layer) {
      (this.track as any).layerName = options.layer;
      (this.fill as any).layerName = options.layer;
      (this.handle as any).layerName = options.layer;
    }

    if (options.position) {
      this.updatePosition(options.position);
    }
  }

  get value(): number {
    return this._value;
  }

  set value(newValue: number) {
    const clampedValue = Math.max(0, Math.min(1, newValue));
    if (this._value !== clampedValue) {
      this._value = clampedValue;
      this.redrawSlider();
      this.onChangeCallback?.(this._value);
    }
  }

  private updatePosition(position: V2d): void {
    this.track.position.set(position.x, position.y);
    this.fill.position.set(position.x, position.y);
    this.handle.position.set(position.x, position.y);
    this.updateBounds();
  }

  private updateBounds(): void {
    if (this.sprite) {
      const position = this.getPosition();
      this.bounds = {
        x: position.x - this.sliderWidth / 2,
        y: position.y - this.handleSize / 2,
        width: this.sliderWidth,
        height: this.handleSize,
      };
    }
  }

  private isPointInside(x: number, y: number): boolean {
    return (
      x >= this.bounds.x &&
      x <= this.bounds.x + this.bounds.width &&
      y >= this.bounds.y &&
      y <= this.bounds.y + this.bounds.height
    );
  }

  private getValueFromX(x: number): number {
    const relativeX = x - this.bounds.x;
    const percentage = Math.max(0, Math.min(1, relativeX / this.sliderWidth));

    // Apply step if specified
    if (this.step > 0) {
      const steps = Math.round(percentage / this.step);
      return steps * this.step;
    }

    return percentage;
  }

  private redrawSlider(): void {
    // Clear all graphics
    this.track.clear();
    this.fill.clear();
    this.handle.clear();

    // Draw track (background)
    this.track.rect(
      -this.sliderWidth / 2,
      -this.trackHeight / 2,
      this.sliderWidth,
      this.trackHeight,
    );
    this.track.stroke({
      color: Colors.WHITE,
      width: 2,
    });

    // Draw fill (active portion)
    const fillWidth = this.sliderWidth * this._value;
    if (fillWidth > 0) {
      this.fill.rect(
        -this.sliderWidth / 2,
        -this.trackHeight / 2,
        fillWidth,
        this.trackHeight,
      );
      this.fill.stroke({
        color: Colors.CYAN,
        width: 2,
      });
    }

    // Draw handle
    const handleX = -this.sliderWidth / 2 + this.sliderWidth * this._value;
    const handleStrokeWidth = this.isDragging ? 4 : this.isHovered ? 3 : 2;

    this.handle.circle(handleX, 0, this.handleSize / 2);
    this.handle.stroke({
      color: Colors.CYAN,
      width: handleStrokeWidth,
    });

    // Add glow effect when hovered or dragging
    if (this.isHovered || this.isDragging) {
      this.handle.circle(handleX, 0, this.handleSize / 2);
      this.handle.stroke({
        color: Colors.CYAN,
        width: 6,
        alpha: 0.3,
      });
    }
  }

  onAdd(): void {
    this.sprite = this.track;
    this.sprites = [this.track, this.fill, this.handle];
    this.redrawSlider();
  }

  onRender(): void {
    // Check hover state
    if (this.game && !this.isDragging) {
      const mousePos = this.game.io.mousePosition;
      const wasHovered = this.isHovered;
      this.isHovered = this.isPointInside(mousePos.x, mousePos.y);

      if (wasHovered !== this.isHovered) {
        this.redrawSlider();
      }
    }

    // Handle dragging
    if (this.isDragging && this.game) {
      const mousePos = this.game.io.mousePosition;
      const newValue = this.getValueFromX(mousePos.x);
      if (newValue !== this._value) {
        this._value = newValue;
        this.redrawSlider();
        this.onChangeCallback?.(this._value);
      }
    }
  }

  onMouseDown(): void {
    if (this.game) {
      const mousePos = this.game.io.mousePosition;
      if (this.isPointInside(mousePos.x, mousePos.y)) {
        this.isDragging = true;
        const newValue = this.getValueFromX(mousePos.x);
        if (newValue !== this._value) {
          this._value = newValue;
          this.redrawSlider();
          this.onChangeCallback?.(this._value);
        }
      }
    }
  }

  onMouseUp(): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.redrawSlider();
    }
  }

  onClick(): void {
    // Click handling is covered by mouseDown
  }

  setPosition(position: V2d): void {
    this.updatePosition(position);
  }

  setValue(value: number): void {
    this.value = value;
  }
}
